import type { EnemyRoleDef } from "./schemas";

export const ENEMY_ROLES: Record<string, EnemyRoleDef> = {
  scout: {
    id: "scout",
    name: "Scout Drone",
    hull: 28,
    shield: 0,
    // Afterburn gives the dart identity without leaving the shared combat plane.
    speed: 32,
    fireCooldown: 1.4,
    damage: 6,
    projectileSpeed: 68,
    threatCost: 1,
    weight: 4,
  },
  skirmisher: {
    id: "skirmisher",
    name: "Skirmisher",
    hull: 45,
    shield: 20,
    speed: 26,
    fireCooldown: 1.15,
    damage: 10,
    projectileSpeed: 82,
    threatCost: 2,
    weight: 3,
  },
  turret: {
    id: "turret",
    name: "Ground Turret",
    hull: 70,
    shield: 30,
    speed: 0,
    fireCooldown: 1.7,
    damage: 14,
    projectileSpeed: 105,
    threatCost: 2,
    weight: 2,
  },
  bomber: {
    id: "bomber",
    name: "Bomber",
    hull: 90,
    shield: 10,
    speed: 16,
    fireCooldown: 2.5,
    damage: 28,
    projectileSpeed: 38,
    threatCost: 3,
    weight: 1,
  },
  jammer: {
    id: "jammer",
    name: "Signal Jammer",
    hull: 55,
    shield: 25,
    speed: 15,
    fireCooldown: 1.85,
    damage: 8,
    projectileSpeed: 58,
    threatCost: 2,
    weight: 2,
  },
  breacher: {
    id: "breacher",
    name: "Breacher",
    hull: 38,
    shield: 0,
    speed: 34,
    fireCooldown: 1.55,
    damage: 16,
    projectileSpeed: 0,
    threatCost: 2,
    weight: 2,
  },
  carrier: {
    id: "carrier",
    name: "Drone Carrier",
    hull: 110,
    shield: 40,
    speed: 11,
    fireCooldown: 2.1,
    damage: 12,
    projectileSpeed: 52,
    threatCost: 4,
    weight: 1,
  },
  sniper: {
    id: "sniper",
    name: "Rail Sniper",
    hull: 62,
    shield: 18,
    speed: 18,
    fireCooldown: 2.6,
    damage: 24,
    projectileSpeed: 150,
    threatCost: 4,
    weight: 1.2,
  },
  scavenger: {
    id: "scavenger",
    name: "Salvage Scavenger",
    hull: 78,
    shield: 34,
    speed: 24,
    fireCooldown: 3,
    damage: 0,
    projectileSpeed: 0,
    threatCost: 3,
    weight: 1.15,
  },
  marauder: {
    id: "marauder",
    name: "Cargo Marauder",
    hull: 96,
    shield: 30,
    speed: 29,
    fireCooldown: 2.4,
    damage: 13,
    projectileSpeed: 0,
    threatCost: 4,
    weight: 0.65,
  },
  tender: {
    id: "tender",
    name: "Aegis Tender",
    hull: 88,
    shield: 72,
    speed: 18,
    fireCooldown: 2.2,
    damage: 9,
    projectileSpeed: 64,
    threatCost: 4,
    weight: 0.8,
  },
  sapper: {
    id: "sapper",
    name: "Fuel Sapper",
    hull: 84,
    shield: 48,
    speed: 20,
    fireCooldown: 2.6,
    damage: 8,
    projectileSpeed: 100,
    threatCost: 5,
    weight: 0.7,
  },
  nullifier: {
    id: "nullifier",
    name: "Guidance Nullifier",
    hull: 108,
    shield: 70,
    speed: 17,
    fireCooldown: 2.15,
    damage: 11,
    projectileSpeed: 76,
    threatCost: 6,
    weight: 0.58,
  },
  leech: {
    id: "leech",
    name: "Capacitor Leech",
    hull: 102,
    shield: 62,
    speed: 19,
    fireCooldown: 1.8,
    damage: 12,
    projectileSpeed: 96,
    threatCost: 7,
    weight: 0.48,
  },
  warden: {
    id: "warden",
    name: "Siege Warden",
    hull: 240,
    shield: 110,
    speed: 13,
    fireCooldown: 1.8,
    damage: 17,
    projectileSpeed: 82,
    threatCost: 10,
    // Boss-arena only; never selected by the ordinary director.
    weight: 0,
  },
  harvester: {
    id: "harvester",
    name: "Dread Harvester",
    hull: 330,
    shield: 160,
    speed: 11,
    fireCooldown: 2,
    damage: 19,
    projectileSpeed: 74,
    threatCost: 14,
    // Rank-36 boss-arena only; never selected by the ordinary director.
    weight: 0,
  },
  sovereign: {
    id: "sovereign",
    name: "Velocity Sovereign",
    hull: 440,
    shield: 220,
    speed: 14,
    fireCooldown: 1.7,
    damage: 22,
    projectileSpeed: 86,
    threatCost: 18,
    weight: 0,
  },
  leviathan: {
    id: "leviathan",
    name: "Foundry Leviathan",
    hull: 560,
    shield: 300,
    speed: 10,
    fireCooldown: 1.55,
    damage: 25,
    projectileSpeed: 80,
    threatCost: 24,
    // Rank-60 boss-arena only; never selected by the ordinary director.
    weight: 0,
  },
  null_crown: {
    id: "null_crown",
    name: "Null Crown",
    hull: 680,
    shield: 380,
    speed: 12,
    fireCooldown: 1.4,
    damage: 28,
    projectileSpeed: 88,
    threatCost: 30,
    // Rank-80 boss-arena only; never selected by the ordinary director.
    weight: 0,
  },
  centurion: {
    id: "centurion",
    name: "Centurion Bastion",
    hull: 820,
    shield: 480,
    speed: 11,
    fireCooldown: 1.3,
    damage: 31,
    projectileSpeed: 92,
    threatCost: 36,
    // Rank-100 boss-arena only; never selected by the ordinary director.
    weight: 0,
  },
  ace: {
    id: "ace",
    name: "Ace Interceptor",
    hull: 180,
    shield: 80,
    speed: 30,
    fireCooldown: 0.85,
    damage: 18,
    projectileSpeed: 95,
    threatCost: 8,
    weight: 0,
  },
};
